import {
	Box,
	Button,
	Collapse,
	debounce,
	Dialog,
	Grid,
	InputAdornment,
	Snackbar,
	Stack,
	Typography
} from '@mui/material';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { DataPipelineModal } from './modal-component';
import { ReactComponent as EditIcon } from '../../assets/edit-img.svg';
import { ReactComponent as DeleteIcon } from '../../assets/delete-img.svg';
import './dataPipeline.scss';
import {
	exportProjectListByProgram,
	getProgramById,
	getProgramSectorList,
	getProjectListByProgram
} from '../../service/service';
import { CustomizedDataGrid } from '../customized_data_grid';
import moment from 'moment';
import { useParams, useNavigate } from 'react-router-dom';
import { usePepwiseAuthController } from 'Context/contexts/AuthContext';
import DataInfoSecondary from 'components/Widgets/DataInfoSecondary';
import PageHeader from 'components/Header/Page';
import DashboardIcon from '../../assets/material-symbols_dashboard.svg';
import ShowInfoWidget from 'components/Widgets/ShowInfoWidget';
import RowTypography from 'components/Table/RowTypography';
import eventEmitter from 'SnackBarEventEmitter';
import DataInfoColorWidget from 'components/Widgets/DataInfoColorWidget';
import SectorFilter from 'components/Dropdown/SectorFilter';
import CustomTextField from 'components/Inputs/CustomTextField';
import { Search } from '@mui/icons-material';
import { checkRole } from 'utils/roleUtils';
import PageWrapper from 'components/Common/PageWraper';
import { useTheme } from "@mui/material/styles";
import { getPaginationSlice, getProjectCommonColumns, getProjectExportConfig, setRdgTableHeight } from './DataPipelineHelper';
import { projectDetailLinkFormatter } from './DataPipelineFormatter';
import ReactDateRangeFilter from 'components/Dropdown/ReactDateRangeFilter';
import IBPFilters from './IBPFilters';
import DependencyGraph from './DependencyGraph';

interface ProjectQueryParams {
    filterInfo?: any, 
	currentFilterType?: string, 
	page?: number, 
	pageSize?: number, 
	stDate?: string, 
	edDate?: string, 
	sortingInfo?: any, 
	sectorId?: string, 
	projName?: string,
	progID?: string
}


const ProgramDetails = () => {
	const theme = useTheme();
	const [controller] = usePepwiseAuthController();
	const { role } = controller;
	const isAllowed = checkRole(role, ['Data Operations Admin', 'PepWise System User']);
	
	const [currentFilterType, setCurrentFilterType] = useState<string>('');
	const [hoveredItem, setHoveredItem] = useState<string | null>(null);
	const [clickedItem, setClickedItem] = useState<string | null>(null);
	const [sectorMasterList, setSectorMasterList] = useState<any[]>([]);
	const [sectorList, setSectorList] = useState<any[]>([]);
	const [sectorName, setSectorName] = useState('all');
	const hiddenSector = "SODASTREAM";
	const filteredSectorList = sectorList.filter(sector => sector !== hiddenSector);

	const handleThreeDotsClose = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const { id, name } = useParams();

	const handleDetailsThreeDotsClick = (event: React.MouseEvent<HTMLElement>) => {

		event.preventDefault();
		event.stopPropagation();
	};
	const handleDetailsThreeDotsClose = (event: any) => {
		event.preventDefault();
		event.stopPropagation();
	};

	const [startDate, setStartDate] = useState(moment().tz("America/Chicago").startOf('day').format('YYYY-MM-DD'));
	const [endDate, setEndDate] = useState(moment().tz("America/Chicago").endOf('day').format('YYYY-MM-DD'));
	const [projectList, setProjectList] = useState<any>([]);
	const [openModal, setOpenModal] = useState<any>({ isOpen: false, message: '' });
	const [programDetail, setProgramDetail] = useState<any>({});
	const [loading, setLoading] = useState(false);
	const [tableLoading, setTableLoading] = useState(false);
	const [paginationData, setPaginationData] = useState({ totalElements: 0, totalPages: 0, currentPage: 0 });
	const [projectsDataList, setProjectsDataList] = useState<any[]>([]);
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(100);
	const defaultColumnNames = ['Sectors', 'Stage', 'Failed', 'Success', 'SLA Status', 'SLA Time (CST)', 'Actual Completion Time (CST)', 'Action'];
	const [filterData, setFilterData] = useState<any[]>([]);
	const [sortingInfo, setSortingInfo] = useState({ sortBy: '', sortDir: '' });
	const searchDetails: any[] = [];
	const [filterInfo, setFilterInfo] = useState<any>({});
	const [searchProject, setSearchProject] = useState('');
	const [toast, setToast] = useState<any>({
		open: false,
		message: ''
	});
	const [autoRefreshInterval, setAutoRefreshInterval] = useState<number | null>(() => {
		const saved = sessionStorage.getItem('IBPAutoRefresh');
		return saved ? Number(saved) : 5;
	});
	const [filteredStartDate, setFilteredStartDate] = useState<string | null>(() => sessionStorage.getItem('IBPStartDate'));
	const [filteredEndDate, setFilteredEndDate] = useState<string | null>(() => sessionStorage.getItem('IBPEndDate'));
	const [savedRangeType, setSavedRangeType] = useState<string | null>(() => sessionStorage.getItem('IBPDateRangeType'));
	const [savedTimeZone, setSavedTimeZone] = useState<string | null>(() => sessionStorage.getItem('IBPDateRangeTimeZone'));
	const [showDependencyGraph, setShowDependencyGraph] = useState(false);

	const navigate = useNavigate();
	const programIdParam = programDetail.Program_Id;
	const [selectedDateFilter, setSelectedDateFilter] = useState<string>('Today');

	const ibpHeaderFilters = useMemo(() => ['report', 'gdo'] as const, []);
	const ibpHeaderDefaultValues = useMemo(
		() => {
			const lastSegment = window.location.pathname.split('/').filter(Boolean).pop()?.toUpperCase();
			const defaultGdo = lastSegment === 'GDO-5' ? 'GDO-5' : 'GDO-3';
			return {
				gdo: defaultGdo,
				report: 'All'
			};
		},
		[]
	);

	const parseInterval = (value: string | null): number | null => {
		if (value === null || value === '0') return null;
		const num = Number(value);
		return isNaN(num) ? null : num;
	};


	useEffect(() => {
		const syncSessionStorage = () => {
			// Auto refresh
			const savedInterval = sessionStorage.getItem('IBPAutoRefresh');
			const parsed = parseInterval(savedInterval);
			setAutoRefreshInterval((prev) => (prev === parsed ? prev : parsed));

			// Date range
			const newRangeType = sessionStorage.getItem('IBPDateRangeType');
			const newTimeZone = sessionStorage.getItem('IBPDateRangeTimeZone');
			const tz = newTimeZone || 'America/Chicago';
			let newStartDate = sessionStorage.getItem('IBPStartDate');
			let newEndDate = sessionStorage.getItem('IBPEndDate');

			if (newRangeType === 'today') {
				const today = moment().tz(tz).format('YYYY-MM-DD');
				newStartDate = today;
				newEndDate = today;
			} else if (newRangeType === '24h') {
				newStartDate = moment().tz(tz).subtract(24, 'hours').format('YYYY-MM-DD');
				newEndDate = moment().tz(tz).format('YYYY-MM-DD');
			} else if (newRangeType === '7d') {
				newStartDate = moment().tz(tz).subtract(6, 'days').format('YYYY-MM-DD');
				newEndDate = moment().tz(tz).format('YYYY-MM-DD');
			} else if (newRangeType === '30d') {
				newStartDate = moment().tz(tz).subtract(29, 'days').format('YYYY-MM-DD');
				newEndDate = moment().tz(tz).format('YYYY-MM-DD');
			}

			setSavedRangeType((prev) => (prev === newRangeType ? prev : newRangeType));
			setSavedTimeZone((prev) => (prev === newTimeZone ? prev : newTimeZone));
			setFilteredStartDate((prev) => (prev === newStartDate ? prev : newStartDate));
			setFilteredEndDate((prev) => (prev === newEndDate ? prev : newEndDate));
		};

		syncSessionStorage();

		window.addEventListener('storage', syncSessionStorage);
		window.addEventListener('ibp-session-storage-updated', syncSessionStorage as any);

		return () => {
			window.removeEventListener('storage', syncSessionStorage);
			window.removeEventListener('ibp-session-storage-updated', syncSessionStorage as any);
		};
	}, []);

	const getColumns = () => {
		return [
			{
				key: 'Sectors',
				name: 'Sectors',
				formatter(props: any) {
					const sectorList = props.row.Sectors ? props.row.Sectors.split(', ') : [];
					if (sectorList && sectorList.length) {
						return sectorList.map((sector: string, idx: number) => <RowTypography sx={{ backgroundColor: 'backgroundLight.dark', color: '#fff', padding: '4px 10px', marginRight: '5px', borderRadius: '20px' }} key={sector} title={sector}>{sector}</RowTypography>);
					} else {
						return '--'
					}
				},
				minWidth: 150,
				maxWidth: 450
			},
			{
				key: 'Project_Name',
				name: 'Project',
				formatter: projectDetailLinkFormatter(
					programDetail?.program_Name,
					startDate,
					endDate,
  					selectedDateFilter
				),
				minWidth: 200,
				maxWidth: 500
			},
			{
				key: 'Stage_Name',
				name: 'Stage',
				formatter: projectDetailLinkFormatter(
					programDetail?.program_Name,
					startDate,
					endDate,
  					selectedDateFilter
				),
				minWidth: 200,
				maxWidth: 500
			},
			...getProjectCommonColumns({ isAllowed, handleAction }),
		];
	};

	const parseFilter = (filterInfo: any, name?: string) => {
		// Call only filterable columns and reset the filtering in case of wrong/empty column
		const parsedFilter: any = {};
		Object.keys(filterInfo).forEach((key: any) => {
			if (key !== name && filterInfo[key] && filterInfo[key].length) {
				parsedFilter[key] = filterInfo[key];
			}
		});
		return parsedFilter;
	};

	const handleFilter = (filterInfo: any) => {

		setFilterInfo(filterInfo);
		setClickedItem(null);

		// if clear all is triggered -> reset widget filter
		const isClearAll = filterInfo && Object.values(filterInfo).every(
			(v: any) => Array.isArray(v) && v.length === 0
		);
		if (isClearAll) {
			setCurrentFilterType('');  
		}

		getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType: '', page, pageSize, stDate: startDate, edDate: endDate, sortingInfo, sectorId: getSectorId()});
	};

	const handleSearch = (e: any) => {
		console.log('FILTER', e);
	};

	const handleSorting = (sortingInfo: any) => {

		getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page, pageSize, stDate: startDate, edDate: endDate, sortingInfo, sectorId: getSectorId()});
		setSortingInfo(sortingInfo)
	};

	const onClickWidget = (type: string) => {
		const filterType = type && (type !== 'Total Projects' && type !== `${sectorName} Projects`) ? type : "";


		setCurrentFilterType(filterType);


		getProjectsByProgram({
			filterInfo: type && type != 'Total Projects' && type !== `${sectorName} Projects` ? {projectSlaStatus: [filterType]} : {},
			currentFilterType: filterType || undefined,
			page,
			pageSize,
			stDate: startDate,
			edDate: endDate,
			sortingInfo,
			sectorId: getSectorId()
		});
	};

	eventEmitter.on('showSnackbar', (message: string) => {
		setLoading(false);
		setTableLoading(false)
	});

	useEffect(() => {
		document.body.classList.add("v2Background");
		const activeId = document.getElementById('root');
		if (window.location.pathname.includes('/data-pipeline/programs')) {
			if (activeId) {
				document.head.classList.add('theme-bg-blue');
				document.body.classList.add('theme-bg-blue');
			}
		}
		if (localStorage.getItem('filterSectors') && localStorage.getItem('sectorsMasterData')) {
			setSectorList(JSON.parse(localStorage.getItem('filterSectors') ?? ''));
			setSectorMasterList(JSON.parse(localStorage.getItem('sectorsMasterData') ?? ''));
		} else {
			getSectorData();
		}
		getProgramDetails(getSectorId(), startDate, endDate);
		getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo, sectorId: getSectorId()});
	}, []);

	const handleSectorChange = (value: string) => {
		setSectorName(value);
		setSearchProject('');
		localStorage.setItem('selectedSector', value);
		let sectorId;
		if (value != 'all') {
			sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == value)[0].Sector_Id
		}
		setProjectList([]);
		getProgramDetails(sectorId, startDate, endDate);
		getProjectsByProgram({filterInfo: currentFilterType ? { projectSlaStatus: [currentFilterType] } : {}, currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo, sectorId});
	};

	const onProjectSearch = (value: string) => {
		setSearchProject(value)
		debouncedFetchOptions(value)
	}

	const debouncedFetchOptions = useMemo(
		() =>
			debounce(async (value) => {

				getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo: { sortBy: '', sortDir: '' }, sectorId: getSectorId(), projName: searchProject || value});

			}, 500),
		[startDate, endDate]
	);




	useEffect(() => {
		if (openModal && !openModal.isOpen && openModal.closeType && openModal.closeType == 'updated') {
			let sectorId;
			if (sectorName && sectorName != 'all') {
				sectorId = sectorMasterList.filter((s: any) => s.Sector_Name == sectorName)[0].Sector_Id
			}
			setSearchProject('');
			getProgramDetails(sectorId, startDate, endDate);
			getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100, stDate: startDate, edDate: endDate, sortingInfo, sectorId});
		}
	}, [openModal]);

	const getSectorData = async () => {
		let sectors: any = await getProgramSectorList();
		const filterSectors = sectors.data.sectors.map((sec: any) => sec.Sector_Name);
		localStorage.setItem('filterSectors', JSON.stringify(filterSectors));
		localStorage.setItem('sectorsMasterData', JSON.stringify(sectors.data.sectors));
		setSectorList(filterSectors);
		setSectorMasterList(sectors.data.sectors);
	};

	const passInfoToParent = (data: any) => {
		getProjectsByProgram({filterInfo: parseFilter(filterInfo), currentFilterType, page: 1, pageSize: 100});
	};

	const getProgramDetails = async (sectorId?: string, fromDate?: string, toDate?: string, progID?: string) => {
		setLoading(true);
		fromDate = normalizeDate(fromDate);
		toDate = normalizeDate(toDate);	
		let result = await getProgramById(progID !== undefined ? progID : id, sectorId, fromDate, toDate);
		setLoading(false);
		setProgramDetail(result.data?.programData ? result.data.programData[0] : '');
		if (!result.data.programData.length) {
			if(window.location.pathname.includes('/ibp-dashboard')){
				navigate(`/data-pipeline/programOps/IBP/markets/ibp-dashboard`);
			}else{
			navigate(`/data-pipeline/dataOps`);
			}
		}
	};

	const normalizeDate = (dateStr?: string) => {
		if (!dateStr) return dateStr;
		const localToday  = moment().format('YYYY-MM-DD');
		const chicagoToday = moment().tz('America/Chicago').format('YYYY-MM-DD');
		// Today filter case
		if (dateStr === localToday  && localToday  !== chicagoToday) {
			return moment(dateStr).tz('America/Chicago').format('YYYY-MM-DD');
		}
		return dateStr;
	};

	const getProjectsByProgram = async ({
        filterInfo,
        currentFilterType,
        page,
        pageSize,
        stDate,
        edDate,
        sortingInfo,
        sectorId,
        projName,
        progID
    }: ProjectQueryParams = {}) => {
		setTableLoading(true);
		stDate = stDate || startDate
		edDate = edDate || endDate
		stDate = normalizeDate(stDate);
		edDate = normalizeDate(edDate);
		sectorId = sectorId || getSectorId()
		const fetchPageNumber = 1;
		const fetchPageSize = 10000;
		let result = await getProjectListByProgram(progID !==undefined ? progID : id, fetchPageNumber, fetchPageSize, currentFilterType, filterInfo, stDate, edDate, sortingInfo, sectorId, projName || '');
		setTimeout(() => {
			setRdgTableHeight(
				`project_list${id}`,
				result.data.projectList.length
			);
		}, 100);
		setTableLoading(false);
		const projData = result?.data?.projectList?.map((list: any) => {
			list['activeRow'] = 0;
			const selectedSectors = list.Sectors && list.Sectors.split(', ');
			list['Sectors'] = selectedSectors && selectedSectors.length ? list.Sectors : "";
			return list;
		});
		setProjectsDataList(projData);
		// Set pagination info (client side)
		const totalElements = projData.length;
  		const totalPages = Math.ceil(totalElements / Number(pageSize || 100));
		setPaginationData({
			totalElements,
			totalPages,
			currentPage: 1
		});
		setPage(1);
  		setProjectList(projData.slice(0, Number(pageSize)));
		const filtersData: any = {
			Sectors: [], Project_Name: [], SLA_Status: []
		}
		if (result.data.filterOptions) {
			Object.entries(result.data.filterOptions).forEach(([key, value]: any) => {
				if (key == 'sector' || key == 'Sector_Name') {
					filtersData['Sectors'] = value;
				}
				else if (key == 'Project_Name' || key == 'projectName') {
					filtersData['Project_Name'] = value;
				} else if (key == 'SLA_Status' || key == 'projectSlaStatus') {
					filtersData['SLA_Status'] = value.filter(Boolean);
				}
			})
		}
		setFilterData(filtersData);
		setFilterInfo(currentFilterType ? filtersData : filterInfo)
	};


	// export
	const exportGetProjectsByProgram = async (filterInfo?: any, currentFilterType?: string, stDate?: string, edDate?: string, sortingInfo?: any, sectorId?: string, projName?: string) => {
		stDate = stDate || startDate
		edDate = edDate || endDate
		
		const payload = getProjectExportConfig({
			filterInfo,
			currentFilterType,
			stDate,
    		edDate,
    		sortingInfo,
    		sectorId: sectorId || getSectorId(),
    		projName,
    		programID: id,
			programName: name
  		});

  		await exportProjectListByProgram(payload);

	};

	const handlePageChange = (event: React.ChangeEvent<unknown>, newPage: number) => {
		setPage(newPage);
		
		const data = getPaginationSlice({
			list: projectsDataList,
			newPage,
			pageSize
		});
		setProjectList(data);
	};

	const handlepageSizeChange = (event: any) => {
		const newPageSize = Number(event.target.value);
  		setPageSize(newPageSize);
  		setPage(1);
		const data = projectsDataList.slice(0, newPageSize);
		setProjectList(data);
    	setPaginationData({
			...paginationData,
    		totalPages: Math.ceil(projectsDataList.length / newPageSize),
   			totalElements: projectsDataList.length,
    		currentPage: 1
		});
	};

	const handleEditClose = (closeType: any) => {
		setOpenModal({
			...openModal,
			isOpen: false,
			closeType: closeType
		});
	};
	const [ibpFilterValues, setIbpFilterValues] = useState<Record<string, string>>({});
	useEffect(() => {
	if (location.pathname?.includes('/ibp-dashboard') && ibpFilterValues?.gdo) {

		const programId = id;

		getProgramDetails(getSectorId(), startDate, endDate, programId);

		getProjectsByProgram({
			filterInfo: filterInfo,
			currentFilterType,
			page: 1,
			pageSize: 100,
			stDate: filteredStartDate? filteredStartDate : startDate,
			edDate: filteredEndDate? filteredEndDate : endDate,
			sortingInfo,
			sectorId: getSectorId(),
			progID: programId
		});
	}
}, [ibpFilterValues, location.pathname, setIbpFilterValues, filteredStartDate, filteredEndDate, ]);


	const onCreateProgramClick = (event: any) => {
		setOpenModal({
			...openModal,
			isOpen: true,
			type: 'Project',
			action: 'CREATE'
		});
		navigate(`/data-pipeline/programs-tab?programID=${programIdParam}&programName=${programDetail.program_Name}&tab=1&isEdited=true`);
	};

	const handleAction = (module: string, type: string, data: any, e: any) => {

		e.preventDefault();
		e.stopPropagation();
		handleThreeDotsClose(e);
		handleDetailsThreeDotsClose(e);
		data = {
			...data,
			ProgramName: programDetail?.program_Name,
			ProgramID: data.Program_ID || data.Program_Id
		};

		switch (type) {
			case 'edit':
				setOpenModal({
					...openModal,
					isOpen: true,
					type: module,
					action: 'EDIT',
					data: data
				});
				if (module == 'Program') {
					navigate(`/data-pipeline/programs-tab?programID=${programIdParam}&programName=${programDetail.program_Name}&isEdited=true&tab=0`);
				} else if (module == 'Project') {
					navigate(`/data-pipeline/programs-tab?programID=${programIdParam}&programName=${programDetail.program_Name}&isEdited=true&tab=1&projectID=${data.Project_Id}`);
				}
				break;
			case 'delete':
				setOpenModal({
					...openModal,
					isOpen: true,
					type: module,
					action: 'DELETE',
					data: data
				});
				break;
			case 'view':
				window.location.href = `/data-pipeline/ibp-dashboard/projects/${data.Program_ID}/${data.Project_Id}/${encodeURIComponent(
					data.ProgramName
				)}/${encodeURIComponent(data.Project_Name)}`;
				break;
			default:
				break;
		}
	};

	const handleClose = () => {
		setToast({
			...toast,
			open: false
		});
	};

	const getToastData = (data: any) => {
		setToast({
			...toast,
			message: data.data.message
		});
	};

	const getSectorId = () => {
		let sectorId;
		let selectedSctr = localStorage.getItem('selectedSector')
		if (selectedSctr && selectedSctr != 'all') {
			setSectorName(selectedSctr)
			const sectorList = JSON.parse(localStorage.getItem('sectorsMasterData') ?? '')
			if (sectorList) {
				sectorId = sectorList.filter((s: any) => s.Sector_Name == selectedSctr)[0].Sector_Id
			}
		}
		return sectorId;
	}

	const handleDateSelect = (stDate: string, edDate: string, selectedDate?: string) => {
		setSearchProject('');
		const sd = moment(stDate).startOf('day').format('YYYY-MM-DD');
		const ed = moment(edDate).startOf('day').format('YYYY-MM-DD');
		setStartDate(sd)
		setEndDate(ed)
		if (selectedDate) {
			setSelectedDateFilter(selectedDate);
		}
		const newFilterInfo = filterInfo?.projectSlaStatus ? { projectSlaStatus: filterInfo.projectSlaStatus } : {};
        setFilterInfo(newFilterInfo);
		if (moment(sd).isValid() && moment(ed).isValid()) {
			getProgramDetails(getSectorId(), sd, ed);
			getProjectsByProgram({filterInfo: newFilterInfo, currentFilterType, page: 1, pageSize: 100, stDate: sd, edDate: ed, sortingInfo, sectorId: getSectorId()});
		}
	}

	let emptyStateContent;
	if (tableLoading) {
		emptyStateContent = (
			<Stack justifyContent="center" alignItems="center" height="60vh">
				<Typography variant="pageTitle" color="dark.main">
					Loading...
				</Typography>
			</Stack>
		);
	} else {
		emptyStateContent = (
			<ShowInfoWidget
				title="No projects have been added yet"
				height="50vh"
				button="+ ADD New Project"
				onClick={onCreateProgramClick}
				isEditable={isAllowed}
			/>
		);
	}

	const isTotalProjects = (title: string) =>
  		title === 'Total Projects' || title.includes('Total Projects');

	const getWidgetClassName = (title: string): string => {
		const isActive = hoveredItem === title || clickedItem === title;

		if (isActive && !isTotalProjects(title)) {
			switch (title) {
				case 'Completed Projects':
				return 'solidBlue';
				case 'SLA Met':
				return 'pgmGreen';
				case 'SLA Missed':
				return 'pgmPink';
				case 'In Progress':
				return 'pgmYellow';
				case 'Scheduled':
				case 'Not Scheduled':
				return 'paleGray';
				default:
				return '';
			}
		}
		return title === 'Not Scheduled' ? 'NotScheduledColor' : 'none';
	};

	const handleMouseEnter = (title: string, value: any) => {
	if (value) {
		setHoveredItem(title);
	}
	};

	const handleMouseLeave = () => {
	setHoveredItem(null);
	};

	const handleWidgetClick = (title: string, value: any, isGrid: boolean = false) => {
		if (!value) return;

		setClickedItem(title);
		onClickWidget((isGrid || !isTotalProjects(title)) ? title : '');
	};

	const handleIbpFiltersChange = useCallback((nextValues: Record<string, string>) => {
		setIbpFilterValues(nextValues);
	}, []);

	return (
		<PageWrapper>
		<Box className={"main-bordered-container"} bgcolor="backgroundLight.primary">
			{!window.location.pathname.includes('/ibp') && (
				<PageHeader
					icon={DashboardIcon}
					title={[
						{
							text: 'Programs',
							link: '/data-pipeline/programOps/IBP/markets/ibp-dashboard'
						},
						{
							text: decodeURIComponent(window.location.pathname.split('/')[4].replace(/%20/g, ' '))
						}
					]}
				>
					<SectorFilter
						sectorName={sectorName}
						setSectorName={setSectorName}
						sectorOptionsList={filteredSectorList}
						handleChange={handleSectorChange}
						boxProps={{
							sx: { minWidth: '200px', position: 'static' },
						}} />
					<ReactDateRangeFilter
						startingDate={startDate}
						endingDate={endDate}
						setStartDate={setStartDate}
						setEndDate={setEndDate}
						handleDateSelect={handleDateSelect}
						initialSelectedDate="Today"
					/>

					{isAllowed && (
						<Button variant="pepwiseSecondary" onClick={onCreateProgramClick} sx={{ height: '43px' }}>
							ADD STAGE
						</Button>
					)}
				</PageHeader>
			)}
			{window.location.pathname.includes('/ibp') && (
				<PageHeader
					icon={DashboardIcon}
					title={[
						{
							text: 'BNL',
							link: '/data-pipeline/programOps/IBP/markets/ibp-dashboard'
						},
						{
							text: decodeURIComponent(window.location.pathname.split('/')[5].replace(/%20/g, ' '))
						}
					]}
				>
					{/* GDO Filter */}
					{/* Report Filter */}
					{isAllowed && location.pathname?.includes('/ibp-dashboard') && (
								<Box sx={{ width: { xs: '100%', md: 'auto' }, minWidth: 0 }}>
										<IBPFilters
											filters={ibpHeaderFilters as any}
											defaultValues={ibpHeaderDefaultValues as any}
											onChange={handleIbpFiltersChange}
										/>
								</Box>
					)}

					{isAllowed && (
						<Button variant="pepwiseSecondary" onClick={onCreateProgramClick} sx={{ height: '43px' }}>
							ADD STAGE
						</Button>
					)}
				</PageHeader>
			)}

			<Dialog open={openModal.isOpen} onClose={handleEditClose} fullWidth={true} maxWidth="md">
				<DataPipelineModal
					handleEditClose={handleEditClose}
					programData={openModal}
					getToastData={getToastData}
					passInfoToParent={passInfoToParent}
				/>
			</Dialog>
			<Box p={3} pr={2} mt={3}>
				<Grid container spacing={2}>
					<Grid item xs={12} lg={5}>
						<DataInfoSecondary
							infoCards={[
								{
									label: 'Program Name',
									value: programDetail?.program_Name,
									isLoading: !programDetail?.program_Name,
									valueProps: {
										lineHeight: '24px'
									}
								}
							]}
							infoTooltip={{
								dateValues: [
									{ label: 'Date Created', value: programDetail?.Created_Dt ?? 'NA' },
									{ label: 'Last Updated', value: programDetail?.Updated_Dt ?? 'NA' }
								]
							}}
							infoOptionMenu={{
								onClick: handleDetailsThreeDotsClick,
								onClose: handleDetailsThreeDotsClose,
								menuItems: [
									{
										key: 'edit',
										label: 'Edit',
										icon: <EditIcon />,
										onMenuClick: (e) => handleAction('Program', 'edit', programDetail, e)
									},
									{
										key: 'delete',
										label: 'Delete',
										icon: <DeleteIcon />,
										onMenuClick: (e) => handleAction('Program', 'delete', programDetail, e)
									}
								]
							}}
							isAdmin={isAllowed ? true : false}
						/>
					</Grid>


					<Grid item container xs={12} lg={7} spacing={1}>
						{[
							{
								title: sectorName != 'all' ? sectorName + ' Projects' : 'Total Projects',
								value: programDetail?.ProjectCount,
								isLoading: loading,
								color: ''
							},
							{
								title: 'Scheduled',
								value: programDetail?.Scheduled_Count || 0,
								isLoading: loading,
								color: 'paleGray'
							},
							{
								title: 'Completed Projects',
								value: (programDetail?.SLA_Met_Count || 0) + (programDetail?.SLA_Missed_Count || 0),
								isLoading: loading,
								color: 'solidBlue'
							},
							{
								title: 'SLA Met',
								value: programDetail?.SLA_Met_Count || 0,
								isLoading: loading,
								color: 'pgmGreen'
							},
							{
								title: 'SLA Missed',
								value: programDetail?.SLA_Missed_Count || 0,
								isLoading: loading,
								color: 'pgmPink'
							},
							{
								title: 'In Progress',
								value: programDetail?.In_Progress_Count || 0,
								isLoading: loading,
								color: 'pgmYellow'
							},
							{
								title: 'Not Scheduled',
								value: programDetail?.Not_Scheduled_Count || 0,
								isLoading: loading,
								color: 'pgmGray',
							},

						].map(({ title, value, color, isLoading }) => (
							<Grid
								key={title}
								item
								xs={3}
								sm={1.7}
								lg={1.7}
								display="flex"
								onMouseEnter={() => handleMouseEnter(title, value)}
								onMouseLeave={handleMouseLeave}
								onClick={() => handleWidgetClick(title, value, true)}
								className="DataInfo"
							>
								<DataInfoColorWidget
									title={window.location.pathname.includes('/ibp') ? title.replace('Projects', 'Stages') : title}
									className={getWidgetClassName(title)}
									style={{ cursor: value ? 'pointer' : 'default' }}
									onClick={() => handleWidgetClick(title, value)}
									value={value}
									boxColor={color}
									isLoading={isLoading}
								/>
							</Grid>
						))}
					</Grid>


				</Grid>
				<Box sx={{ position: 'relative' }}>
					<Typography mt={3} variant="overlayWidgetTitle" sx={{ position: 'absolute', color: `${theme.palette.table.title} !important` }} component="span">
						{window.location.pathname.includes('/ibp') ? 'Stages' : 'Projects'}
					</Typography>
					{programDetail?.ProjectCount > 0 && <CustomTextField
						placeholder="Search Projects"
						value={searchProject}
						onChange={(e) => onProjectSearch(e.target.value)}
						style={{ width: '22rem', position: 'absolute', top: '11px', right: '70px', backgroundColor: `${theme.palette.search.background} !important`, borderRadius: '5px', zIndex: '99' }}
						sx={{
							backgroundColor: `${theme.palette.search.background} !important`
						}}
						InputLabelProps={{
							sx: {
								backgroundColor: `${theme.palette.search.background} !important`
							}
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<Search sx={{ color: 'white.main' }} />
								</InputAdornment>
							),
							sx: {
								'& .MuiInputBase-input': {
									padding: '14px',
									paddingLeft: 0
								},
								'& .MuiOutlinedInput-notchedOutline': {
									borderColor: 'dark.dark',
									borderWidth: '0px',
									borderStyle: 'solid'
								},
								'&:hover .MuiOutlinedInput-notchedOutline': {
									borderColor: 'dark.dark',
									borderWidth: '0px'
								},
								'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
									borderColor: 'dark.dark',
									borderWidth: '0px'
								}
							}
						}}
					/>
					}
				</Box>					{programDetail?.ProjectCount > 0 && !tableLoading ? (
						<Box sx={{ position: 'relative' }}>

						{/* Dependency Graph Section */}
						<Box sx={{ mt: 2, mb: 1 }}>
							<Button
								variant="text"
								size="small"
								onClick={() => setShowDependencyGraph(!showDependencyGraph)}
								sx={{ textTransform: 'none', fontWeight: 600, color: 'primary.main' }}
							>
								{showDependencyGraph ? 'Hide' : 'Show'} Dependency Graph
							</Button>
							<Collapse in={showDependencyGraph}>
								<Box sx={{ mt: 1 }}>
									<DependencyGraph market={sectorName !== 'all' ? sectorName : undefined} businessProgram="IBP" height={500} />
								</Box>
							</Collapse>
						</Box>



						{projectList?.length > 0 && (
							<Box>
								<CustomizedDataGrid
									tableColumns={getColumns()}
									tableData={projectList}
									tableType="program-list"
									defaultColumnNames={defaultColumnNames}
									onSorting={handleSorting}
									currSorting={sortingInfo}
									filterDataOptions={filterData}
									searchDataValues={searchDetails}
									onFilterCheck={handleFilter}
									currFilter={filterInfo}
									onFilterSearch={handleSearch}
									page={page}
									pageSize={pageSize}
									paginationData={paginationData}
									listLength={projectsDataList.length}
									handlepageSizeChange={handlepageSizeChange}
									handlePageChange={handlePageChange}
									isFetching={tableLoading}
									showExport={true}
									fileName={`programs_Page_${paginationData.currentPage}_of_${paginationData.totalPages}.xlsx`}
									exportTableData={() => {
										const parsed = parseFilter(filterInfo);
										const widgetFilter = currentFilterType;
										return exportGetProjectsByProgram(parsed, widgetFilter, startDate, endDate, sortingInfo, getSectorId(), ""); 
									}}
									isDateRangeFilter={false}
									isDisabled={false}
									isShowMoreButton
								/>
							</Box>
						)}
						{!projectList?.length && <Stack justifyContent="center" alignItems="center" height="60vh">
							<Typography variant="pageTitle" color="dark.main">
								No data for the selected filters
							</Typography>
						</Stack>}
					</Box>
				) : emptyStateContent}
			</Box>
			<Snackbar
				open={toast && openModal.message ? true : false}
				anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
				autoHideDuration={6000}
				onClick={handleClose}
				message={openModal.message}
			/>
		</Box>
		</PageWrapper>
	);
};

export default ProgramDetails;
